import { useState, useEffect } from "react";
import MainLogo from "../img/LogoO@2x.png";
import MainLogoPhone from "../img/LogoO@2x.png";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Image,
  Stack,
  useBreakpointValue,
  useDisclosure,
  Portal,
  Text,
} from "@chakra-ui/react";

export default function Nav() {
  const { i18n, t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isScrolled, setIsScrolled] = useState(false);

  const accentColor = "#c6a664";
  const imageSrc = useBreakpointValue({ base: MainLogoPhone, md: MainLogo });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLang = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    document.dir = newLang === "ar" ? "rtl" : "ltr";
    if (isOpen) onClose();
  };

  const menuItems = [
    { label: t("navbar.home"), to: "hero" },
    { label: t("navbar.about"), to: "overview" },
    { label: t("navbar.visionMission"), to: "vision-mission" },
    { label: t("navbar.services"), to: "services" },
    { label: t("navbar.partners"), to: "partners" },
    { label: t("navbar.careers"), to: "careers" },
    { label: t("navbar.contact"), to: "contact" },
  ];


  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      w="100%"
      zIndex="2000"
      transition="all 0.4s ease"
      bg={isScrolled ? "rgba(15,17,25,0.9)" : "transparent"}
      backdropFilter={isScrolled ? "blur(14px)" : "none"}
      borderBottom={isScrolled ? "1px solid rgba(255,255,255,0.08)" : "none"}
      boxShadow={isScrolled ? "0 6px 25px rgba(0,0,0,0.3)" : "none"}
    >
      <Container maxW="container.xl" py={{ base: 3, md: 4 }}>
        <Flex justify="space-between" align="center">
          {/* Logo */}
          <Image
            src={imageSrc}
            alt="Insight ALC Logo"
            h={useBreakpointValue({ base: "42px", md: "58px" })}
            w="auto"
          />

          {/* Desktop Menu */}
          <HStack spacing={8} display={{ base: "none", md: "flex" }}>
            {menuItems.map((item, i) => (
              <ScrollLink
                key={i}
                to={item.to}
                smooth
                duration={700}
                offset={-80}
              >
                <Text
                  color="whiteAlpha.900"
                  fontWeight="medium"
                  fontSize="md"
                  cursor="pointer"
                  transition="all 0.3s ease"
                  _hover={{ color: accentColor }}
                >
                  {item.label}
                </Text>
              </ScrollLink>
            ))}

            <Button
              size="sm"
              variant="outline"
              borderColor="#fff"
              color="#fff"
              _hover={{
                bg: accentColor,
                color: "black",
                borderColor: accentColor,
              }}
              onClick={toggleLang}
            >
              {i18n.language === "ar" ? "English" : "عربي"}
            </Button>
          </HStack>

          {/* Mobile Toggle */}
          <IconButton
            aria-label="Toggle Menu"
            bg="transparent"
            color="white"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            display={{ base: "flex", md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            fontSize="22px"
            _hover={{ bg: "rgba(255,255,255,0.1)" }}
          />
        </Flex>
      </Container>

      {/* Mobile Menu */}
      <Portal>
        <Box
          position="fixed"
          top={isOpen ? "0" : "-100vh"}
          left="0"
          w="100%"
          h="100vh"
          bg="rgba(15,17,25,0.96)"
          backdropFilter="blur(16px)"
          transition="top 0.4s ease"
          zIndex="1999"
          display={{ base: "flex", md: "none" }}
          flexDir="column"
          alignItems="center"
          justifyContent="center"
        >
          <Stack spacing={6} align="center">
            {menuItems.map((item, index) => (
              <ScrollLink
                key={index}
                to={item.to}
                smooth
                duration={700}
                offset={-80}
                onClick={onClose}
              >
                <Text
                  color="white"
                  fontSize="2xl"
                  fontWeight="semibold"
                  _hover={{ color: accentColor }}
                >
                  {item.label}
                </Text>
              </ScrollLink>
            ))}

            <Button
              mt={6}
              size="md"
              variant="outline"
              borderColor="#fff"
              color="#fff"
              _hover={{
                bg: accentColor,
                color: "black",
                borderColor: accentColor,
              }}
              onClick={toggleLang}
            >
              {i18n.language === "ar" ? "English" : "عربي"}
            </Button>
          </Stack>
        </Box>
      </Portal>
    </Box>
  );
}
